import { Button, Input, Textarea } from '@evaneos/b2b-ui/legacy';
import { ChangeEvent, useState } from 'react';
import { FormattedMessage, IntlShape, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Loader } from '../../../design-system/Loader';

import s from './MessageTemplatesForm.module.css';

type Props = {
    initialContent?: string;
    intl: IntlShape;
    initialTitle?: string;
    isSubmitting: boolean;
    submit: (params: { title: string; content: string }) => void;
};

type FormError = { title: boolean; content: boolean };

const FormComponent = ({
    initialContent = '',
    intl: { formatMessage },
    initialTitle = '',
    isSubmitting,
    submit,
}: Props) => {
    const [content, setContent] = useState<string>(initialContent);
    const [title, setTitle] = useState<string>(initialTitle);
    const [isValid, setValidity] = useState<FormError>({
        title: true,
        content: true,
    });

    const isPristine = initialTitle === title && initialContent === content;

    const validateForm = () => {
        if (title === '' || content === '') {
            setValidity({ content: content !== '', title: title !== '' });
            return false;
        }
        return true;
    };

    const changeTitle = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setTitle(value);
        setValidity({ ...isValid, title: true });
    };
    const changeContent = ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
        setContent(value);
        setValidity({ ...isValid, content: true });
    };

    const submitForm = () => {
        if (validateForm() && !isSubmitting) {
            submit({ title, content });
        }
    };
    return (
        <div className={s.messageTemplateForm}>
            <div>
                <div className={s.inputContainer}>
                    <Input
                        className={s.input}
                        data-testid="message-template-form-title"
                        id="message-template-title"
                        name="message-template-title"
                        error={!isValid.title}
                        onChange={changeTitle}
                        label={formatMessage({ id: 'templates.form.title' })}
                        required
                        value={title}
                        errorText={formatMessage({
                            id: 'form.errors.required',
                        })}
                        style={{ width: '100%' }}
                    />
                </div>
                <div className={s.inputContainer}>
                    <Textarea
                        id="message-template-content"
                        className={s.textarea}
                        data-testid="message-template-form-content"
                        name="message-template-content"
                        error={!isValid.content}
                        label={formatMessage({ id: 'templates.form.content' })}
                        required
                        value={content}
                        onChange={changeContent}
                        errorText={formatMessage({
                            id: 'form.errors.required',
                        })}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>
            <div className={s.submit}>
                <Link to={{ pathname: '/templates/list' }} replace>
                    <Button data-testid="message-template-form-cancel" priority="secondary">
                        <FormattedMessage id="form.cancel" />
                    </Button>
                </Link>
                <Button
                    className={s.button}
                    data-testid="message-template-form-submit"
                    disabled={isPristine}
                    onClick={submitForm}
                >
                    {isSubmitting ? (
                        <Loader className={s.loader} />
                    ) : (
                        <FormattedMessage id="form.submit" />
                    )}
                </Button>
            </div>
        </div>
    );
};

export const Form = injectIntl(FormComponent);
